import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Plus, Trash2, Eye, Printer, ScanBarcode, PackagePlus, PenLine } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, todayStr, bn, money, netRate } from "../lib/format";
import ContactPicker from "../components/ContactPicker";
import { PageHeader, Field, Modal, Empty } from "../components/kit";
import ProductSearch from "../components/ProductSearch";
import BanglaInput from "../components/BanglaInput";
import CustomerPicker from "../components/CustomerPicker";
import ProductForm from "../components/ProductForm";
import BarcodeScanner from "../components/BarcodeScanner";
import ScaledMemo from "../components/ScaledMemo";

let KEY = 1;
const beep = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const o = ctx.createOscillator();
    o.frequency.value = 1200;
    o.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.08);
  } catch { /* no audio */ }
};

function ItemRow({ it, i, upd, del }: any) {
  return (
    <tr data-testid={`memo-line-${i}`}>
      <td className="text-slate-500 num">{bn(i + 1)}</td>
      <td className="min-w-[180px]">
        {it.product_id ? (
          <div><p className="font-semibold">{it.name}</p>{it.qty > it.stock && <p className="text-xs text-amber-600">স্টকে আছে: {bn(it.stock)}</p>}</div>
        ) : (
          <BanglaInput className="inp inp-sm" placeholder="পণ্যের নাম লিখুন" value={it.name} onChange={(v) => upd({ name: v })} testid={`memo-line-item-name-input-${i}`} />
        )}
      </td>
      <td><input className="inp inp-sm w-20 num" type="number" step="any" min="0" value={it.qty} onChange={(e) => upd({ qty: e.target.value })} data-testid={`memo-line-item-qty-input-${i}`} /></td>
      <td><input className="inp inp-sm w-20" value={it.unit} onChange={(e) => upd({ unit: e.target.value })} data-testid={`memo-line-item-unit-input-${i}`} /></td>
      <td><input className="inp inp-sm w-24 num" type="number" step="any" min="0" value={it.rate} onChange={(e) => upd({ rate: e.target.value })} data-testid={`memo-line-item-rate-input-${i}`} /></td>
      <td><input className="inp inp-sm w-24" value={it.disc || ""} onChange={(e) => upd({ disc: e.target.value })} placeholder="যেমন ১০+২+২" data-testid={`memo-line-item-disc-input-${i}`} /></td>
      <td className="text-right num font-semibold whitespace-nowrap" data-testid={`memo-line-total-${i}`}>{money((+it.qty || 0) * netRate(it.rate, it.disc))}</td>
      <td><button type="button" className="w-8 h-8 grid place-items-center rounded-lg text-red-500 hover:bg-red-50" onClick={del} data-testid={`memo-line-remove-${i}`} aria-label="মুছুন"><Trash2 size={16} /></button></td>
    </tr>
  );
}

function SumRow({ label, value, cls = "", testid }: any) {
  return (
    <div className={`flex justify-between py-1.5 ${cls}`}>
      <span>{label}</span>
      <span className="num font-semibold" data-testid={testid}>{tk(value)}</span>
    </div>
  );
}

export default function MemoCreate({ barcodeMode }: { barcodeMode?: boolean }) {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [customer, setCustomer] = useState<any>(null);
  const [nc, setNc] = useState({ name: "", mobile: "", address: "" });
  const [salesman, setSalesman] = useState(localStorage.getItem("re_salesman") || "");
  const [date, setDate] = useState(todayStr());
  const [items, setItems] = useState<any[]>([]);
  const [dType, setDType] = useState("flat");
  const [dVal, setDVal] = useState("");
  const [paid, setPaid] = useState("");
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");
  const [quick, setQuick] = useState<any>(null);
  const [cam, setCam] = useState(false);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const cid = sp.get("customer");
    if (cid) api.get(`/customers/${cid}`).then((r) => setCustomer(r.data.customer)).catch(() => null);
  }, [sp]);

  useEffect(() => {
    if (barcodeMode) scanRef.current?.focus();
  }, [barcodeMode]);

  const addProduct = useCallback((p: any) => {
    setItems((list) => {
      const ex = list.find((x) => x.product_id === p.id);
      if (ex) return list.map((x) => (x === ex ? { ...x, qty: (+x.qty || 0) + 1 } : x));
      return [...list, { key: KEY++, product_id: p.id, name: p.name, qty: 1, unit: p.unit, rate: p.rate, stock: p.stock }];
    });
  }, []);

  const addManual = () => setItems((l) => [...l, { key: KEY++, product_id: null, name: "", qty: 1, unit: "পিস", rate: "", disc: "" }]);

  const lookup = useCallback(async (c: string) => {
    const v = c.trim();
    if (!v) return;
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(v)}`);
      addProduct(data);
      beep();
      toast.success(`${data.name} যোগ হয়েছে`);
    } catch {
      toast.error("এই বারকোডের পণ্য পাওয়া যায়নি", { action: { label: "দ্রুত যোগ", onClick: () => setQuick({ barcode: v }) } });
    }
  }, [addProduct]);

  const onCam = useCallback((c: string) => { setCam(false); lookup(c); }, [lookup]);

  const subtotal = useMemo(() => items.reduce((s, it) => s + (+it.qty || 0) * netRate(it.rate, it.disc), 0), [items]);
  const disc = dType === "percent" ? (subtotal * (+dVal || 0)) / 100 : +dVal || 0;
  const grand = Math.max(0, subtotal - disc);
  const prevDue = customer?.due || 0;
  const receivable = prevDue + grand;
  const paidN = +paid || 0;
  const curDue = receivable - paidN;
  const custName = customer?.name || nc.name;

  const draft = {
    invoice_no: "প্রিভিউ", date, customer: customer || { name: nc.name || "নগদ ক্রেতা", mobile: nc.mobile, address: nc.address }, salesman,
    items: items.map((it) => { const q = +it.qty || 0; const nr = netRate(it.rate, it.disc); return { ...it, qty: q, mrp: +it.rate || 0, disc: it.disc || "", rate: +it.rate || 0, total: q * nr }; }), subtotal, discount_type: dType, discount_value: +dVal || 0,
    discount_amount: disc, grand_total: grand, previous_due: prevDue, total_receivable: receivable, paid: paidN, current_due: curDue, note,
  };

  const save = async () => {
    if (!items.length) return toast.error("অন্তত একটি পণ্য যোগ করুন");
    if (items.some((it) => !String(it.name).trim() || !(+it.qty > 0))) return toast.error("প্রতিটি লাইনে পণ্যের নাম ও পরিমাণ দিন");
    if (curDue > 0 && !custName.trim()) return toast.error("বাকি রাখতে হলে ক্রেতার নাম দিন");
    setBusy(true);
    localStorage.setItem("re_salesman", salesman);
    try {
      const { data } = await api.post("/memos", {
        customer_id: customer?.id || null, customer_name: nc.name, customer_mobile: nc.mobile, customer_address: nc.address, salesman, date,
        items: items.map((it) => ({ product_id: it.product_id, name: String(it.name).trim(), qty: +it.qty, unit: it.unit, rate: +it.rate || 0, disc: it.disc || "" })),
        discount_type: dType, discount_value: +dVal || 0, paid: paidN, note,
      });
      toast.success(`মেমো ${data.invoice_no} সেভ হয়েছে`);
      nav(`/memos/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="memo-create-page">
      <PageHeader title={barcodeMode ? "বারকোড সেল" : "নতুন ক্যাশ মেমো"} subtitle="পণ্য খুঁজুন, বারকোড স্ক্যান করুন অথবা নিজে লাইন লিখুন" />
      <div className="grid xl:grid-cols-[1fr_360px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <div className="card p-4 md:p-5">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="ক্রেতা (সংরক্ষিত)">
                <CustomerPicker value={customer} onSelect={setCustomer} />
              </Field>
              <Field label="তারিখ"><input className="inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="memo-date-input" /></Field>
            </div>
            {!customer && (
              <div className="grid sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-dashed border-slate-200">
                <Field label="নতুন ক্রেতার নাম"><BanglaInput className="inp" value={nc.name} onChange={(v) => setNc({ ...nc, name: v })} placeholder="খালি থাকলে: নগদ ক্রেতা" testid="memo-new-customer-name" /></Field>
                <Field label="মোবাইল">
                  <div className="flex gap-2">
                    <input className="inp flex-1" value={nc.mobile} onChange={(e) => setNc({ ...nc, mobile: e.target.value })} data-testid="memo-new-customer-mobile" />
                    <ContactPicker testid="memo-customer-contact-picker" onPick={(c) => setNc((p) => ({ ...p, mobile: c.tel || p.mobile, name: p.name?.trim() ? p.name : (c.name || "") }))} />
                  </div>
                </Field>
                <Field label="ঠিকানা"><BanglaInput className="inp" value={nc.address} onChange={(v) => setNc({ ...nc, address: v })} testid="memo-new-customer-address" /></Field>
              </div>
            )}
          </div>

          <div className="card p-4 md:p-5">
            <div className="grid lg:grid-cols-[1.3fr_1fr] gap-3">
              <ProductSearch onPick={addProduct} onQuickAdd={(name: string) => setQuick({ name })} />
              <form className={`flex gap-2 rounded-lg ${barcodeMode ? "ring-2 ring-blue-500 ring-offset-2" : ""}`} onSubmit={(e) => { e.preventDefault(); lookup(code); setCode(""); }}>
                <div className="relative flex-1">
                  <ScanBarcode size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input ref={scanRef} className="inp pl-10" value={code} onChange={(e) => setCode(e.target.value)} placeholder="বারকোড স্ক্যান / লিখে Enter" data-testid="barcode-scanner-input" />
                </div>
                <button type="button" className="btn btn-ghost px-3" onClick={() => setCam(true)} data-testid="memo-camera-scan-button" aria-label="ক্যামেরা"><Camera size={18} /></button>
              </form>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" className="btn btn-ghost btn-sm" onClick={addManual} data-testid="memo-add-item-button"><PenLine size={15} /> ম্যানুয়াল লাইন</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuick({})} data-testid="memo-quick-add-product-button"><PackagePlus size={15} /> দ্রুত পণ্য যোগ</button>
            </div>
            <div className="mt-4 overflow-x-auto -mx-4 md:mx-0">
              {items.length === 0 ? <Empty text="এখনো কোনো পণ্য যোগ হয়নি" /> : (
                <table className="tbl" data-testid="memo-items-editor">
                  <thead><tr><th>#</th><th>পণ্য</th><th>পরিমাণ</th><th>একক</th><th>দর/MRP (৳)</th><th>ছাড় %</th><th className="text-right">মোট</th><th /></tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <ItemRow key={it.key} it={it} i={i}
                        upd={(p: any) => setItems((l) => l.map((x) => (x.key === it.key ? { ...x, ...p } : x)))}
                        del={() => setItems((l) => l.filter((x) => x.key !== it.key))} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {items.length > 0 && (
              <button type="button" className="mt-3 text-sm font-semibold text-blue-700 inline-flex items-center gap-1" onClick={addManual} data-testid="memo-add-another-line"><Plus size={15} /> আরেকটি লাইন</button>
            )}
          </div>
        </div>

        <div className="card p-5 xl:sticky xl:top-20 space-y-3" data-testid="memo-summary-panel">
          <h2 className="text-lg font-semibold">হিসাব</h2>
          <div className="text-[15px] divide-y divide-slate-100">
            <SumRow label="সাবটোটাল" value={subtotal} testid="memo-subtotal-display" />
            <div className="flex items-center justify-between gap-2 py-2">
              <span>ছাড়</span>
              <div className="flex gap-1.5">
                <select className="inp inp-sm w-16" value={dType} onChange={(e) => setDType(e.target.value)} data-testid="memo-discount-type-select">
                  <option value="flat">৳</option><option value="percent">%</option>
                </select>
                <input className="inp inp-sm w-24 num" type="number" min="0" step="any" value={dVal} onChange={(e) => setDVal(e.target.value)} placeholder="0" data-testid="memo-discount-input" />
              </div>
            </div>
            <SumRow label="সর্বমোট" value={grand} cls="text-blue-800 text-lg font-bold" testid="memo-grand-total-display" />
            <SumRow label="পূর্বের বাকি" value={prevDue} testid="memo-previous-due-display" />
            <SumRow label="মোট প্রাপ্য" value={receivable} cls="font-semibold" testid="memo-receivable-display" />
          </div>
          <Field label="পরিশোধ (৳)">
            <div className="flex gap-2">
              <input className="inp num text-lg font-semibold" type="number" min="0" step="any" value={paid} onChange={(e) => setPaid(e.target.value)} placeholder="0" data-testid="memo-paid-amount-input" />
              <button type="button" className="btn btn-ghost btn-sm h-11" onClick={() => setPaid(String(+receivable.toFixed(2)))} data-testid="memo-pay-full-button">সব</button>
            </div>
          </Field>
          <div className={`rounded-lg px-4 py-3 flex justify-between items-center ${curDue > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
            <span className="font-semibold">বর্তমান বাকি</span>
            <span className="num text-xl font-bold" data-testid="memo-current-due-display">{tk(curDue)}</span>
          </div>
          {paidN > receivable && <p className="text-sm text-red-600">পরিশোধ মোট প্রাপ্যের চেয়ে বেশি</p>}
          <Field label="নোট (ঐচ্ছিক)"><BanglaInput className="inp" value={note} onChange={(v) => setNote(v)} testid="memo-note-input" /></Field>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button type="button" className="btn btn-ghost" onClick={() => setPreview(true)} disabled={!items.length} data-testid="memo-preview-button"><Eye size={17} /> প্রিভিউ</button>
            <button type="button" className="btn btn-green" onClick={save} disabled={busy || paidN > receivable} data-testid="memo-save-and-print-button"><Printer size={17} /> {busy ? "সেভ..." : "সেভ ও প্রিন্ট"}</button>
          </div>
        </div>
      </div>

      <Modal open={!!quick} onClose={() => setQuick(null)} title="দ্রুত নতুন পণ্য যোগ" testid="quick-add-product-modal">
        {quick && <ProductForm compact initial={quick} onCancel={() => setQuick(null)} onSaved={(p) => { addProduct(p); setQuick(null); }} />}
      </Modal>
      <BarcodeScanner open={cam} onClose={() => setCam(false)} onScan={onCam} />
      <Modal open={preview} onClose={() => setPreview(false)} title="মেমো প্রিভিউ" wide testid="memo-preview-modal">
        <div className="bg-slate-100 p-2 rounded-lg"><ScaledMemo memo={draft} /></div>
      </Modal>
    </div>
  );
}
